/// <reference path="../../lib/services/myaccount-services.d.ts" />

import _ from 'lodash';
import BaseAccountController from '../../lib/controllers/BaseAccountController';

export default class ManageAddressesController extends BaseAccountController {
	public static $inject: string[] = ['$scope', 'UserResource', 'UserAddressResource', 'CountryResource'];

	public updateSuccessful: boolean;
	public user: MyAccountApi.IUser;
	public Countries: MyAccountApi.ICountryResource;
	public countryOptions: MyAccountApi.ICountry[];
	public primaryAddress?: MyAccountApi.IUserAddress;
	public additionalAddresses: MyAccountApi.IUserAddress[];
	public generalMessage: string;
	public newAddress: MyAccountApi.IUserAddress | null;
	public generalErrors: {
		[key: string]: string
	};
	public generalMessages: {
		[key: string]: string
	};
	public selections: {
		[key: string]: {
			country: MyAccountApi.ICountry | null,
			state: MyAccountApi.IStateProvince | null
		}
	};
	public addressPattern: RegExp;
	public namePattern: RegExp;

	public deleteDialogAddress: MyAccountApi.IUserAddress;
	public deleteDialogLoading: boolean;

	private readonly User: MyAccountApi.IUserResource;
	private readonly UserAddress: MyAccountApi.IUserAddressResource;
	private readonly zipCodePatterns: Array<RegExp | null> = [null, //
		/^\d{5}(-\d{4})?$/, // US (valId 1)
		/^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1}\s?\d{1}[A-Z]{1}\d{1}$/i, // Canada (valId 2)
		/^\d{5}$/]; // Mexico (valId 3)

	constructor($scope: ng.IScope, User: MyAccountApi.IUserResource, UserAddress: MyAccountApi.IUserAddressResource, Countries: MyAccountApi.ICountryResource) {
		super($scope);

		this.User = User;
		this.UserAddress = UserAddress;
		this.Countries = Countries;
		this.additionalAddresses = [];
		this.selections = {};
		this.generalErrors = {};
		this.generalMessages = {};
		this.loadInitialData();

		this.addressPattern = /^[a-zA-Z0-9\s#\',./\-]*$/;
		this.namePattern = /^[a-zA-Z0-9\s#',.-]*$/;
	}

	public canEdit(address: MyAccountApi.IUserAddress): boolean {
		// Non-household addresses can be edited by anyone, while household (membership) addresses can only be edited
		// by a primary or authorized member
		return !address.isHousehold || (this.user.memberContext != null && this.user.memberContext.isPrimaryOrAuthorized);
	}

	public countrySelected(address: MyAccountApi.IUserAddress, country: MyAccountApi.ICountry): void {
		address.address.country = country.description;
	}

	public stateSelected(address: MyAccountApi.IUserAddress, state: MyAccountApi.IStateProvince): void {
		address.address.state = state.description;
	}

	public canDelete(address: MyAccountApi.IUserAddress): string {
		return this.canEdit(address) ? '' : 'You do not have permission to Edit this address.';
	}

	public deleteAddress(address: MyAccountApi.IUserAddress): void {
		this.generalMessage = '';

		const canDeleteError: string = this.canDelete(address);
		if (canDeleteError) {
			const errorKey: string = address === this.primaryAddress ? 'primary' : address.addressLabel;

			this.generalMessages[errorKey] = '';
			this.generalErrors[errorKey] = canDeleteError;

			return;
		}

		this.deleteDialogLoading = false;
		this.deleteDialogAddress = address;

		$('#deleteModal').modal();
	}

	public deleteAddressConfirmed(): void {
		this.deleteDialogLoading = true;

		const errorKey: string = this.deleteDialogAddress === this.primaryAddress ? 'primary' : this.deleteDialogAddress.addressLabel;

		this.deleteDialogAddress.$delete(() => {
			const index = this.additionalAddresses.indexOf(this.deleteDialogAddress);
			this.additionalAddresses.splice(index, 1);

			this.generalErrors[errorKey] = '';
			this.generalMessage = `You’ve successfully deleted "${this.deleteDialogAddress.addressLabel}".`;

			$('#deleteModal').modal('hide');
		}, (failure: MyAccountApi.IServiceFailureResponse ) => {
			this.generalMessages[errorKey] = '';

			if (failure && failure.data && failure.data.validationErrors) {
				this.generalErrors[errorKey] = '';

				if (failure.data.validationErrors[0].message.indexOf('Autoship') >= 0) {
					this.generalErrors[errorKey] = 'A valid address is required with your Autoship account.'
						+ ' You can delete after updating your Autoship address <a href="https://shop.lifetimefitness.com">here</a>.';
				} else {
					failure.data.validationErrors.forEach((err) => {
						this.generalErrors[errorKey] += `${err.message}\n`;
					});
				}
			} else {
				this.generalErrors[errorKey] = 'Error while deleting address';
			}

			$('#deleteModal').modal('hide');
		});
	}

	public addNewAddress(): void {
		const country = this.primaryAddress!.address.country;
		const state = this.primaryAddress!.address.state;
		this.newAddress = new this.UserAddress();
		this.newAddress.address = {
			street1: '',
			street2: '',
			city: '',
			state,
			country,
			zipCode: ''
		};

		this.labelChanged(this.newAddress);
	}

	public labelChanged(address: MyAccountApi.IUserAddress, ctrl?: ng.INgModelController): void {
		const label = address.addressLabel;

		const labelIsUnique = !this.hasDuplicateLabel(address);

		if (!labelIsUnique && ctrl) {
			ctrl.$setValidity('unique', false);
			ctrl.$setViewValue('');
			return;
		}

		this.selectCountry(address.address.country, label);
		this.selectState(address.address.state, label);
		this.generalErrors[label] = '';

		if (ctrl) {
			this.validateAddressLabel(address, ctrl);
		}
	}

	public cancelAddNew(): void {
		this.newAddress = null;
		this.generalErrors.newAddress = '';
	}

	public saveNewAddress(form: ng.IFormController, address: MyAccountApi.IUserAddress): void {
		const updateAddressError = this.canUpdateAddress(form, address);
		const errorKey: string = address === this.primaryAddress ? 'primary' : address.addressLabel;

		this.generalMessage = '';
		if (!updateAddressError) {
			address.$create(() => {
				this.additionalAddresses.push(address);
				this.newAddress = null;
				this.generalErrors[errorKey] = '';
				this.generalMessages[errorKey] = 'You’ve successfully added your address.';
			}, (err: string) => {
				this.generalMessages[errorKey] = '';
				this.generalErrors[errorKey] = err;
			});
		} else {
			this.generalMessages[errorKey] = '';
			this.generalErrors[errorKey] = updateAddressError;
		}
	}

	public canUpdateAddress(form: ng.IFormController, address: MyAccountApi.IUserAddress): string | undefined {
		let canUpdateError: string | undefined;

		if (!form.$valid) {
			canUpdateError = 'Sorry, this seems to be an invalid address. Please review highlighted fields below for errors.';
		}

		return canUpdateError;
	}

	public updateAddress(form: ng.IFormController, address: MyAccountApi.IUserAddress): string | undefined {
		const canUpdateError = this.canUpdateAddress(form, address);
		const errorKey: string = address === this.primaryAddress ? 'primary' : address.addressLabel;
		this.generalMessage = '';
		this.generalMessages[errorKey] = '';

		if (!canUpdateError) {
			address.$save(() => {
				this.generalErrors[errorKey] = '';
				this.generalMessages[errorKey] = 'You’ve successfully updated your address.';
			}, (err: string) => {
				this.generalMessages[errorKey] = '';
				this.generalErrors[errorKey] = 'Unable to update address due to server error. Please try again in a minute.';
			});
		} else {
			this.generalMessages[errorKey] = '';
			this.generalErrors[errorKey] = canUpdateError;
		}

		return canUpdateError;
	}

	public validateAddressLabel(address: MyAccountApi.IUserAddress, ctrl: ng.INgModelController): void {
		ctrl.$setValidity('unique', !address.addressLabel
			|| this.hasDuplicateLabel(address) != null
			|| address.addressLabel !== this.primaryAddress!.addressLabel);
	}

	public getZipCodePattern(country: MyAccountApi.ICountry): RegExp {
		return this.zipCodePatterns[country.valId]!;
	}

	/**
	 * Triggered whenever SSO ID is available/changed
	 */
	protected loadData(): void {
		// load My Account user details
		this.user = this.User.get();

		this.UserAddress.query((addresses: MyAccountApi.IUserAddress[]) => {
			this.primaryAddress = _.find(addresses, (address) => {
				return address.isHousehold;
			});

			// if no household address is found (which'll always be true for non-members),
			// denote the first address as the primary address
			if (!this.primaryAddress && addresses.length > 0) {
				this.primaryAddress = addresses[0];
			}

			if (this.primaryAddress != null) {
				this.selectCountry(this.primaryAddress.address.country, 'primary');
				this.selectState(this.primaryAddress.address.state, 'primary');
				this.generalErrors.primary = '';

				const primaryIndex = addresses.indexOf(this.primaryAddress);

				addresses.splice(primaryIndex, 1);
			}

			addresses.forEach((address) => {
				this.labelChanged(address);
			});

			this.additionalAddresses = addresses;
		});
	}

	/**
	 * Triggered whenever profile data is available/changed
	 */
	protected profileDataLoaded(): void {
		return;
	}

	private hasDuplicateLabel(address: MyAccountApi.IUserAddress): MyAccountApi.IUserAddress | undefined {
		return _.find(this.additionalAddresses, (addr) => {
			return this.hasSameLabel(addr, address);
		});
	}

	private hasSameLabel(addr: MyAccountApi.IUserAddress, address: MyAccountApi.IUserAddress): boolean {
		return addr.addressLabel === address.addressLabel
			&& addr.contactMethodId !== address.contactMethodId;
	}

	private selectCountry(countryVal: string, addressLabel: string): void {
		if (!this.selections[addressLabel]) {
			this.selections[addressLabel] = {
				country: null,
				state: null
			};
		}

		this.selections[addressLabel].country = this.getCountry(countryVal);
	}

	private selectState(stateVal: string, addressLabel: string): void {
		if (!this.selections[addressLabel]) {
			this.selections[addressLabel] = {
				country: null,
				state: null
			};
		}

		this.selections[addressLabel].state = this.getState(this.selections[addressLabel].country!, stateVal);
	}

	private getCountry(countryVal: string): MyAccountApi.ICountry {
		return _.find(this.countryOptions, (country) => {
			return country.description === countryVal;
		})!;
	}

	private getState(country: MyAccountApi.ICountry, stateVal: string): MyAccountApi.IStateProvince {
		return _.find(country.stateList, (state) => {
			return state.description === stateVal.trim();
		})!;
	}

	private loadInitialData(): void {
		this.countryOptions = this.Countries.query();
	}
}
